import React, { useEffect, useMemo, useRef, useState } from 'react';

function initials(label) {
  const parts = String(label || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const AVATAR_COLORS = ['#2f5fdb', '#7c3aed', '#0891b2', '#c2410c', '#16a34a', '#db2777', '#4f46e5'];
function avatarColor(key) {
  const str = String(key ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export default function CompanySelect({
  companies = [],
  value = '',
  onChange,
  placeholder = 'All companies',
  disabled = false,
  loading = false,
  variant = 'pill',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const items = useMemo(
    () => companies.map((c) => ({
      id: c.id,
      label: c.name_firma || c.name || `#${c.id}`,
      shortName: c.short_name || '',
    })),
    [companies]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((it) => it.label.toLowerCase().includes(q) || it.shortName.toLowerCase().includes(q));
  }, [items, search]);

  const selected = items.find((it) => String(it.id) === String(value));

  useEffect(() => {
    if (!open) return undefined;
    setHighlight(0);
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    const focusTimer = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
      clearTimeout(focusTimer);
    };
  }, [open]);

  const pick = (id) => {
    onChange?.(id ? String(id) : '');
    setOpen(false);
    setSearch('');
  };

  const handleListKeyDown = (e) => {
    const optionCount = filtered.length + 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, optionCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight === 0) pick('');
      else pick(filtered[highlight - 1]?.id);
    }
  };

  return (
    <div className={`ivs-company-select ivs-company-select--${variant}`} ref={rootRef}>
      <button
        type="button"
        className="ivs-company-select__trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <span className="ivs-company-select__avatar" style={{ background: avatarColor(selected.id) }}>
            {initials(selected.shortName || selected.label)}
          </span>
        ) : (
          <span className="ivs-company-select__avatar ivs-company-select__avatar--all">
            <i className="bi bi-buildings"></i>
          </span>
        )}
        <span className="ivs-company-select__label">
          {loading ? 'Loading…' : selected ? selected.label : placeholder}
        </span>
        <i className={`bi bi-chevron-down ivs-company-select__chevron ${open ? 'is-open' : ''}`}></i>
      </button>

      {open && (
        <div className="ivs-company-select__panel" role="listbox" onKeyDown={handleListKeyDown}>
          <div className="ivs-company-select__search">
            <i className="bi bi-search"></i>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search companies…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="ivs-company-select__list">
            <div
              role="option"
              aria-selected={!value}
              className={`ivs-company-select__option ${!value ? 'is-selected' : ''} ${highlight === 0 ? 'is-highlighted' : ''}`}
              onMouseEnter={() => setHighlight(0)}
              onClick={() => pick('')}
            >
              <span className="ivs-company-select__avatar ivs-company-select__avatar--all">
                <i className="bi bi-buildings"></i>
              </span>
              <span className="ivs-company-select__option-label">{placeholder}</span>
              {!value && <i className="bi bi-check2 ivs-company-select__check"></i>}
            </div>

            {filtered.length === 0 ? (
              <div className="ivs-company-select__empty">No companies found</div>
            ) : (
              filtered.map((it, idx) => (
                <div
                  key={it.id}
                  role="option"
                  aria-selected={String(value) === String(it.id)}
                  className={`ivs-company-select__option ${String(value) === String(it.id) ? 'is-selected' : ''} ${highlight === idx + 1 ? 'is-highlighted' : ''}`}
                  onMouseEnter={() => setHighlight(idx + 1)}
                  onClick={() => pick(it.id)}
                >
                  <span className="ivs-company-select__avatar" style={{ background: avatarColor(it.id) }}>
                    {initials(it.shortName || it.label)}
                  </span>
                  <span className="ivs-company-select__option-label">
                    {it.label}
                    {it.shortName && <span className="ivs-company-select__option-sub"> {it.shortName}</span>}
                  </span>
                  {String(value) === String(it.id) && <i className="bi bi-check2 ivs-company-select__check"></i>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
